import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowUp, ArrowUpToLine, X } from 'lucide-react';
import { API } from '../../App';

/**
 * FileRow — renders a single context entry with a provenance badge.
 *
 * Badge priority (mutually exclusive, top wins):
 *   1. role === 'instructions'        → INST          (yellow)
 *   2. generated === true             → GENERATED <label> (purple, link to source fray)
 *   3. source === 'project'           → UPLOADED      (green)
 *   4. (default)                      → no badge
 *
 * `promoted === true` shows a separate ↑ glyph regardless of badge state
 * (set by Phase 4 when a fray-context is promoted to project scope).
 */
export default function FileRow({ context = {}, onClick, projectId, frayId, onPromoted, onDetach }) {
  const {
    title,
    filename,
    context_id,
    role,
    generated,
    source,
    source_fray_id,
    source_fray_label,
    promoted,
  } = context;

  const canPromote = source === 'fray' && projectId && frayId && context_id;
  const canDetach = source === 'project' && projectId && context_id && onDetach && role !== 'instructions';
  const [promoting, setPromoting] = React.useState(false);
  const [detaching, setDetaching] = React.useState(false);

  const promote = async (e) => {
    e.stopPropagation();
    if (!canPromote || promoting) return;
    setPromoting(true);
    try {
      await API.post(`/api/projects/${projectId}/contexts`, {
        context_id,
        role: role || 'reference',
      });
      await API.delete(`/api/frays/${frayId}/contexts/${context_id}`);
      if (onPromoted) onPromoted();
    } catch (err) {
      // best-effort; surface in console for now
      // eslint-disable-next-line no-console
      console.error('Promote failed:', err);
    }
    setPromoting(false);
  };

  const detach = async (e) => {
    e.stopPropagation();
    if (!canDetach || detaching) return;
    if (!window.confirm(`Detach "${title || filename || context_id.slice(0, 8)}" from this project? The context itself is preserved.`)) return;
    setDetaching(true);
    try {
      await API.delete(`/api/projects/${projectId}/contexts/${context_id}`);
      if (onDetach) onDetach();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Detach failed:', err);
    }
    setDetaching(false);
  };

  const displayName = title || filename || (context_id || '').slice(0, 8);

  let badge = null;
  if (role === 'instructions') {
    badge = (
      <span
        className="px-1.5 py-0.5 text-[9px] font-mono uppercase rounded bg-[#F2A900]/20 text-[#F2A900]"
        data-testid="badge-inst"
      >
        INST
      </span>
    );
  } else if (generated) {
    const text = `GENERATED ${source_fray_label || ''}`.trim();
    badge = source_fray_id ? (
      <Link
        to={`/fray/${source_fray_id}/results`}
        data-testid="generated-source-link"
        className="px-1.5 py-0.5 text-[9px] font-mono uppercase rounded bg-[#A855F7]/20 text-[#A855F7] hover:bg-[#A855F7]/30"
      >
        {text}
      </Link>
    ) : (
      <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase rounded bg-[#A855F7]/20 text-[#A855F7]">
        {text}
      </span>
    );
  } else if (source === 'project') {
    badge = (
      <span
        className="px-1.5 py-0.5 text-[9px] font-mono uppercase rounded bg-[#10B981]/20 text-[#10B981]"
        data-testid="badge-uploaded"
      >
        UPLOADED
      </span>
    );
  }

  const tooltip = [
    role && `role: ${role}`,
    source && `source: ${source}`,
    source_fray_label && `from: ${source_fray_label}`,
    generated && 'generated by fray',
    promoted && 'promoted to project scope',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      onClick={onClick}
      title={tooltip || displayName}
      className={`flex items-center gap-2 py-1 px-2 text-xs font-mono text-[#F5F1E8] ${
        onClick ? 'cursor-pointer hover:bg-[#1E1E1E]' : ''
      }`}
      data-testid={`file-row-${context_id}`}
    >
      <FileText className="w-3 h-3 text-[#A0A0A0] shrink-0" />
      <span className="flex-1 truncate">{displayName}</span>
      {promoted && (
        <ArrowUp
          data-testid="promoted-glyph"
          className="w-3 h-3 text-[#10B981] shrink-0"
          aria-label="promoted to project scope"
        />
      )}
      {canPromote && (
        <button
          type="button"
          onClick={promote}
          disabled={promoting}
          data-testid={`promote-${context_id}`}
          className="p-1 text-[#A0A0A0] hover:text-[#10B981] disabled:opacity-50"
          title="Promote to project scope (auto-flow into all frays)"
          aria-label="Promote to project scope"
        >
          <ArrowUpToLine className="w-3 h-3" />
        </button>
      )}
      {canDetach && (
        <button
          type="button"
          onClick={detach}
          disabled={detaching}
          data-testid={`detach-${context_id}`}
          className="p-1 text-[#A0A0A0] hover:text-[#A61E17] disabled:opacity-50"
          title="Detach from project (the context itself is preserved)"
          aria-label="Detach from project"
        >
          <X className="w-3 h-3" />
        </button>
      )}
      {badge}
    </div>
  );
}
